import React, { useEffect } from 'react';

import { DatePicker, Divider, Form, Modal, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface NewGeoVictoriaMigrationModalProps {
  isNewGeoVictoriaMigrationModalOpen: boolean;
  handleNewGeoVictoriaMigrationModalOk: (
    startDate: string,
    endDate: string,
    frequency: 'diaria' | 'mensual'
  ) => void;
  handleNewGeoVictoriaMigrationModalCancel: () => void;
  loading: boolean;
}

const { RangePicker } = DatePicker;

function NewGeoVictoriaMigrationModal({
  isNewGeoVictoriaMigrationModalOpen,
  handleNewGeoVictoriaMigrationModalOk,
  handleNewGeoVictoriaMigrationModalCancel,
  loading,
}: NewGeoVictoriaMigrationModalProps) {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const dateFormat: string = useSelector(
    (s: any) => s.intl?.dateFormat || 'DD/MM/YYYY'
  );

  useEffect(() => {
    if (isNewGeoVictoriaMigrationModalOpen) {
      form.setFieldValue('range', null);
      form.setFieldValue('frequency', null);
    }
  }, [isNewGeoVictoriaMigrationModalOpen]);

  const onFinish = (values: any) => {
    handleNewGeoVictoriaMigrationModalOk(
      values.range[0].format('YYYY-MM-DD'),
      values.range[1].format('YYYY-MM-DD'),
      values.frequency
    );
  };

  const validateDateRange = (_: any, value: any) => {
    if (!value || value.length < 2) {
      return Promise.resolve();
    }
    const [start, end] = value;
    const frequency = form.getFieldValue('frequency');
    const currentYear = new Date().getFullYear();

    if (frequency === 'diaria') {
      if (start.year() !== currentYear || end.year() !== currentYear) {
        return Promise.reject(
          new Error(
            t(
              'scenes.geovictoria.geo_victoria_to_buk_migration.components.new_migration_modal.validations.current_year_required'
            )
          )
        );
      }
      if (start.month() !== end.month() || start.year() !== end.year()) {
        return Promise.reject(
          new Error(
            t(
              'scenes.geovictoria.geo_victoria_to_buk_migration.components.new_migration_modal.validations.same_month_year_required'
            )
          )
        );
      }
    }

    if (end.diff(start, 'days') > 31) {
      return Promise.reject(
        new Error(
          t(
            'scenes.geovictoria.geo_victoria_to_buk_migration.components.new_migration_modal.validations.range_max_days_exceeded'
          )
        )
      );
    }
    return Promise.resolve();
  };

  const handleFrequencyChange = () => {
    form.validateFields(['range']);
  };

  return (
    <Modal
      title={t(
        'scenes.geovictoria.geo_victoria_to_buk_migration.components.new_migration_modal.title'
      )}
      open={isNewGeoVictoriaMigrationModalOpen}
      onOk={() => {
        form.submit();
      }}
      onCancel={handleNewGeoVictoriaMigrationModalCancel}
      okText={t(
        'scenes.geovictoria.geo_victoria_to_buk_migration.components.new_migration_modal.ok_text'
      )}
      maskClosable={false}
      confirmLoading={loading}
      destroyOnClose
    >
      <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="range"
          label={t(
            'scenes.geovictoria.geo_victoria_to_buk_migration.components.new_migration_modal.labels.range'
          )}
          rules={[
            {
              required: true,
              message: t(
                'scenes.geovictoria.geo_victoria_to_buk_migration.components.new_migration_modal.validations.required'
              ),
            },
            { validator: validateDateRange },
          ]}
        >
          <RangePicker
            allowClear={false}
            style={{ width: '100%' }}
            format={dateFormat}
          />
        </Form.Item>
        <Form.Item
          name="frequency"
          label={t(
            'scenes.geovictoria.geo_victoria_to_buk_migration.components.new_migration_modal.labels.frequency'
          )}
          rules={[
            {
              required: true,
              message: t(
                'scenes.geovictoria.geo_victoria_to_buk_migration.components.new_migration_modal.validations.required'
              ),
            },
          ]}
        >
          <Select
            style={{ width: '100%' }}
            placeholder={t(
              'scenes.geovictoria.geo_victoria_to_buk_migration.components.new_migration_modal.placeholders.select_option'
            )}
            allowClear
            onChange={handleFrequencyChange}
          >
            <Select.Option key="diaria" value="diaria">
              {t(
                'scenes.geovictoria.geo_victoria_to_buk_migration.components.new_migration_modal.options.daily'
              )}
            </Select.Option>
            <Select.Option key="mensual" value="mensual">
              {t(
                'scenes.geovictoria.geo_victoria_to_buk_migration.components.new_migration_modal.options.monthly'
              )}
            </Select.Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default NewGeoVictoriaMigrationModal;
